'use client'

import React, { ReactNode, useCallback, useEffect } from 'react'

import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  OffsetOptions,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react-dom-interactions'

import { Z_INDICES } from 'Constants/ZIndices'

type Trigger = 'click' | 'hover'

type Props = {
  children: ReactNode
  content: ReactNode
  offsetOptions?: OffsetOptions
  placement?: 'top' | 'bottom' | 'left' | 'right'
  trigger?: Trigger
  delay?: number
  open?: boolean
  onClickOutside?: (event: Event) => void
  onOpenChange?: (value: boolean) => void
}

function Popover({
  children,
  content,
  offsetOptions = 8,
  placement = 'bottom',
  trigger = 'click',
  delay = 0,
  open,
  onClickOutside,
  onOpenChange,
}: Props) {
  const { refs, x, y, strategy, context, floating, reference } =
    useFloating<HTMLDivElement>({
      open,
      onOpenChange: nextOpen => {
        onOpenChange?.(nextOpen)
      },
      placement,
      middleware: [offset(offsetOptions), flip(), shift()],
      whileElementsMounted: autoUpdate,
    })

  const hover = useHover(context, {
    enabled: trigger === 'hover',
    delay,
    handleClose: safePolygon(),
  })
  const click = useClick(context, { enabled: trigger === 'click' })
  const dismiss = useDismiss(context)
  const role = useRole(context)

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    click,
    dismiss,
    role,
  ])

  const handleClickOutside = useCallback(
    (event: Event) => {
      const refElement = refs.reference.current
      const floatElement = refs.floating.current

      const isDomNode = (element: unknown): element is Node =>
        element instanceof Node && 'contains' in element

      if (
        isDomNode(refElement) &&
        isDomNode(floatElement) &&
        !refElement.contains(event.target as Node) &&
        !floatElement.contains(event.target as Node)
      ) {
        onClickOutside?.(event)
        onOpenChange?.(false)
      }
    },
    [onClickOutside, onOpenChange, refs],
  )

  useEffect(() => {
    if (!open) return
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open, handleClickOutside])

  return (
    <>
      <div ref={reference} {...getReferenceProps()}>
        {children}
      </div>
      {open && (
        <FloatingPortal>
          <FloatingFocusManager
            context={context}
            initialFocus={-1}
            modal
            visuallyHiddenDismiss
          >
            <div
              ref={floating}
              style={{
                position: strategy,
                top: y ?? 0,
                left: x ?? 0,
                zIndex: Z_INDICES.POPOVER,
              }}
              {...getFloatingProps()}
            >
              {content}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  )
}

export default Popover
